import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import CustomValidator from '@core/helper/custom-validation.helper';
import FormControlHelper from '@core/helper/form-control.helper';
import { FieldType, JsonFormData, JsonFormControl } from '@shared/models/json-form.model';
import { startCase } from 'lodash';

@Component({
    selector: 'eig-dynamic-json-form',
    templateUrl: './dynamic-json-form.component.html',
    styleUrls: ['./dynamic-json-form.component.scss']
})
export class DynamicJsonFormComponent implements OnInit, OnChanges {
    @Input() jsonFormData: JsonFormData;
    @Input() data: any;
    @Input() previewButton: boolean = false;
    @Output() onSave: EventEmitter<{}> = new EventEmitter<{}>();
    @Output() onPreview: EventEmitter<{}> = new EventEmitter<{}>();
    myForm: FormGroup;

    constructor(private formBuilder: FormBuilder) {}

    ngOnInit(): void {
        this.myForm = this.generateFilterForm();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.jsonFormData && !changes.jsonFormData.firstChange) {
            this.myForm = this.generateFilterForm();
            this.patchFormData();
        }
        if (changes.data && !changes.data.firstChange) {
            this.patchFormData();
        }
    }

    private generateFilterForm(): FormGroup {
        const baseForm = this.formBuilder.group({});
        this.jsonFormData.data.forEach((field) => {
            baseForm.addControl(field.name, this.generateFormGroup(baseForm, field));
        });
        return baseForm;
    }

    private patchFormData() {
        this.myForm.reset();
        this.processMapData();
        this.myForm.patchValue(this.data);
        this.patchFormArrays(this.myForm, this.data, this.jsonFormData.data);
    }

    private processMapData() {
        this.jsonFormData.data.forEach((fieldData) => {
            if (fieldData.type === FieldType.Map) {
                const mapData = this.data[fieldData.name] || {};
                this.data[fieldData.name] = Object.keys(mapData).map((k) => {
                    return { key: k, value: mapData[k] };
                });
            }
        });
    }

    patchFormArrays(myForm: FormGroup, data: any, jsonData: JsonFormControl[]) {
        //Patch form arrays, add new form array controls based on number of entries
        if (jsonData.length == 0) {
            return;
        }
        Object.keys(data).forEach((key) => {
            if (Array.isArray(data[key])) {
                const formArray = myForm.get(key) as FormArray;
                const field = jsonData.filter((k) => k.name == key);
                const isArrayOrMapField =
                    field.length > 0 &&
                    (field[0].type === FieldType.Array || field[0].type === FieldType.Map) &&
                    data[key].length > formArray.length;
                if (isArrayOrMapField) {
                    const count = data[key].length - formArray.length;
                    for (let i = 0; i < count; i++) {
                        this.addToFormArray(formArray, field[0]);
                    }
                    if (field[0].group) {
                        for (let i = 0; i < data[key].length; i++) {
                            this.patchFormArrays(formArray.at(i) as FormGroup, data[key][i], field[0].group);
                        }
                    }
                }
                formArray.patchValue(data[key]);
            } else if (data[key] != null && typeof data[key] === 'object') {
                let filteredJsonData = jsonData.filter((k) => k.name == key);
                if (filteredJsonData.length > 0) {
                    this.patchFormArrays(myForm.get(key) as FormGroup, data[key], filteredJsonData[0].group);
                }
            }
        });
    }

    generateFormGroup(baseForm: FormGroup, field: JsonFormControl): FormGroup {
        if (!field.label) {
            field.label = startCase(field.name);
        }
        if (field.type == FieldType.Group && field.group) {
            const formGroup = this.formBuilder.group({});
            field.group.forEach((item) => {
                formGroup.addControl(item.name, this.generateFormGroup(formGroup, item));
            });
            return formGroup;
        } else if (field.type == FieldType.Array) {
            const formArray = this.formBuilder.array([]);
            this.addToFormArray(formArray, field);
            baseForm.addControl(field.name, formArray);
        } else if (field.type === FieldType.Map) {
            const formArray = this.formBuilder.array([]);
            this.addToFormArray(formArray, field);
            baseForm.addControl(field.name, formArray);
        } else {
            if (field.type == FieldType.Number) {
                field.validators ? (field.validators['number'] = true) : (field.validators = { number: true });
            }
            const validatorsToAdd = [];
            for (const [key, value] of Object.entries(field.validators || [])) {
                switch (key) {
                    case 'required':
                        if (value) {
                            validatorsToAdd.push(Validators.required);
                        }
                        break;
                    case 'number':
                        if (value) {
                            validatorsToAdd.push(CustomValidator.onlyNumberValidator);
                        }
                        break;
                    default:
                        break;
                }
            }
            baseForm.addControl(field.name, this.formBuilder.control('', validatorsToAdd));
        }
        return baseForm;
    }

    addToFormArray(formArray: FormArray, field: JsonFormControl) {
        if (field.group) {
            const formGroup = this.formBuilder.group({});
            field.group.forEach((item) => {
                formGroup.addControl(item.name, this.generateFormGroup(formGroup, item));
            });
            formArray.push(formGroup);
        } else {
            formArray.push(this.formBuilder.control(''));
        }
    }

    deleteFromFormArray(formArray: FormArray, index) {
        formArray.removeAt(index);
    }

    saveForm() {
        if (this.myForm.valid) {
            this.onSave.emit(this.removeEmpty(this.myForm.value));
        } else {
            FormControlHelper.validateAllFields(this.myForm);
        }
    }

    previewConfig() {
        this.onPreview.emit(this.removeEmpty(this.myForm.value));
    }

    private removeEmpty(obj: {}) {
        let newObj = {};
        Object.keys(obj).forEach((k) => {
            if (!this.isEmpty(obj[k])) {
                if (Array.isArray(obj[k])) {
                    const arrayData = obj[k].filter((v) => !this.isEmpty(v));
                    const isMapObject =
                        arrayData.length > 0 &&
                        typeof arrayData[0] === 'object' &&
                        'key' in arrayData[0] &&
                        'value' in arrayData[0];
                    if (isMapObject) {
                        newObj[k] = arrayData.reduce((acc, current) => {
                            acc[current.key] = current.value;
                            return acc;
                        }, {});
                    } else {
                        newObj[k] = arrayData;
                    }
                } else if (typeof obj[k] == 'object') {
                    newObj[k] = this.removeEmpty(obj[k]);
                } else {
                    newObj[k] = obj[k];
                }
            }
        });
        return newObj;
    }

    private isEmpty(val: any) {
        if (val === null || val === '') {
            return true;
        }
        if (Array.isArray(val)) {
            return val == [] || val.every((v) => this.isEmpty(v));
        } else if (typeof val === 'object') {
            return val == {} || Object.values(val).every((x) => this.isEmpty(x));
        }
    }
}
